/**
 * AppsFlyer reads. Module-private async fns returned from a factory; each
 * unwraps the BE `{ data: T }` envelope and throws ApiError on failure
 * (status uses getOrNullOn404 so a not-yet-connected brand is an empty
 * state, not an error).
 */
import type {
  AdminBrandOverview,
  AppsflyerApp,
  AppsflyerStatus,
  CampaignHealth,
  CampaignReport,
  CreatorCampaignLink,
  SeriesPoint,
} from '@discovr/types';
import { adminBase, brandBase, campaignBase, dateRangeQs, getData, getOrNullOn404 } from './shared';

const getStatus = (brandId: string): Promise<AppsflyerStatus | null> =>
  getOrNullOn404<AppsflyerStatus>(`${brandBase(brandId)}/status`);

const listApps = async (brandId: string): Promise<AppsflyerApp[]> => {
  const body = await getData<{ apps: AppsflyerApp[] }>(`${brandBase(brandId)}/apps`);
  return body.apps ?? [];
};

const listEvents = async (brandId: string, appId: string): Promise<string[]> => {
  const body = await getData<{ events: string[] }>(
    `${brandBase(brandId)}/events?app_id=${encodeURIComponent(appId)}`,
  );
  return body.events ?? [];
};

const listTemplates = async (brandId: string): Promise<string[]> => {
  const body = await getData<{ templates: string[] }>(`${brandBase(brandId)}/onelink-templates`);
  return body.templates ?? [];
};

const listLinks = async (campaignId: string): Promise<CreatorCampaignLink[]> => {
  const body = await getData<{ links: CreatorCampaignLink[] }>(`${campaignBase(campaignId)}/links`);
  return body.links ?? [];
};

const getReport = (campaignId: string, from?: string, to?: string): Promise<CampaignReport> =>
  getData<CampaignReport>(`${campaignBase(campaignId)}/report${dateRangeQs({ from, to })}`);

const getSeries = async (
  campaignId: string,
  from?: string,
  to?: string,
): Promise<SeriesPoint[]> => {
  const body = await getData<{ series: SeriesPoint[] }>(
    `${campaignBase(campaignId)}/report/series${dateRangeQs({ from, to })}`,
  );
  return body.series ?? [];
};

const getHealth = (campaignId: string): Promise<CampaignHealth> =>
  getData<CampaignHealth>(`${campaignBase(campaignId)}/attribution/health`);

const getAdminBrandOverview = (): Promise<AdminBrandOverview> =>
  getData<AdminBrandOverview>(`${adminBase()}/brands`);

export function createAppsflyerQueriesApi() {
  return {
    getStatus,
    listApps,
    listEvents,
    listTemplates,
    listLinks,
    getReport,
    getSeries,
    getHealth,
    getAdminBrandOverview,
  };
}

export type AppsflyerQueriesApi = ReturnType<typeof createAppsflyerQueriesApi>;
